import { Outlet, useLocation, Navigate } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import Sidebar from "./Sidebar";
import TopBar from "./TopBar";
import AnnouncementBanner from "../announcements/AnnouncementBanner";
import PlatformAnnouncementBanner from "../announcements/PlatformAnnouncementBanner";
import OnboardingWizard from "../OnboardingWizard";
import DemoTour, { DEMO_TOUR_FLAG } from "../DemoTour";
import DmWidget from "../dm/DmWidget";
import OfflineIndicator from "../offline/OfflineIndicator";
import MustChangePasswordDialog from "../MustChangePasswordDialog";
import { useAuthStore } from "../../store/authStore";
import { useServiceWorkerNavigation } from "../../hooks/useServiceWorkerNavigation";
import { initOnlineTracker } from "../../store/onlineStore";
import { useSwipeGesture } from "../../lib/useSwipeGesture";

export default function AppShell() {
  const { user, fetchMe } = useAuthStore();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showDemoTour, setShowDemoTour] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();

  // Demo login account gets a guided tour instead of the setup wizard —
  // its workspace is already seeded, so "create your first project" makes
  // no sense there.
  const isDemoAccount = user?.email === "user1@serpixtest.com";

  // Push notifications: react-router navigation via SW postMessage.
  useServiceWorkerNavigation();

  // Boot the online/offline tracker + outbox drainer once.
  useEffect(() => {
    const teardown = initOnlineTracker();
    return teardown;
  }, []);

  // Close the mobile drawer whenever the route changes.
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Lock body scroll while the mobile drawer is open.
  useEffect(() => {
    if (sidebarOpen) {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "";
      };
    }
  }, [sidebarOpen]);

  const closeSidebar = useCallback(() => setSidebarOpen(false), []);
  const openSidebar = useCallback(() => setSidebarOpen(true), []);

  // Swipe gestures: right-edge-drag to open, swipe-left to close (mobile only)
  useSwipeGesture({ open: sidebarOpen, onOpen: openSidebar, onClose: closeSidebar });

  // Always refresh the current user's record when the route changes or the
  // window regains focus. Keeps flags like `can_create_projects`,
  // `active`, `role` in sync when a super admin toggles them for someone
  // currently signed in.
  useEffect(() => {
    fetchMe?.();
  }, [location.pathname, fetchMe]);

  useEffect(() => {
    const onFocus = () => fetchMe?.();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [fetchMe]);

  useEffect(() => {
    if (
      user &&
      !isDemoAccount &&
      user.role === "super_admin" &&
      user.onboarding_completed === false
    ) {
      setShowOnboarding(true);
    }
  }, [user?.id, user?.onboarding_completed, isDemoAccount]);

  // Kick off the demo tour once the shell is mounted. The flag is set by
  // the "Try demo account" button on /login; cleared when the tour ends.
  useEffect(() => {
    if (!isDemoAccount) return;
    let flagged = false;
    try {
      flagged = sessionStorage.getItem(DEMO_TOUR_FLAG) === "1";
    } catch {
      /* ignore */
    }
    if (!flagged) return;
    const t = setTimeout(() => setShowDemoTour(true), 700);
    return () => clearTimeout(t);
  }, [isDemoAccount]);

  const closeDemoTour = useCallback(() => {
    setShowDemoTour(false);
    try {
      sessionStorage.removeItem(DEMO_TOUR_FLAG);
    } catch {
      /* ignore */
    }
  }, []);

  // Platform admins live in their own dedicated shell — bounce them there
  // if they land on any tenant-scoped route.
  if (user?.role === "platform_admin") {
    return <Navigate to="/app/platform" replace />;
  }

  // Newly invited TEAM MEMBERS (role: "user") get a dedicated full-page
  // 5-step Linear-style onboarding at /app/onboarding-welcome. Super
  // admins keep the existing modal wizard (their flow is workspace setup,
  // a different mental model).  We only redirect if they're not already
  // on that route to avoid a loop.
  const onOnboardingRoute = location.pathname.startsWith("/app/onboarding-welcome");
  if (
    user &&
    user.role !== "super_admin" &&
    user.role !== "ultra_admin" &&
    user.role !== "platform_admin" &&
    user.onboarding_completed === false &&
    !onOnboardingRoute
  ) {
    return <Navigate to="/app/onboarding-welcome" replace />;
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      <Sidebar open={sidebarOpen} onClose={closeSidebar} />
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/45 backdrop-blur-[2px] md:hidden"
          onClick={closeSidebar}
          data-testid="sidebar-backdrop"
        />
      )}
      <div className="md:ml-64">
        <TopBar onMenuOpen={openSidebar} />
        <PlatformAnnouncementBanner />
        <AnnouncementBanner />
        <main className="p-3 sm:p-6 md:p-8">
          <Outlet />
        </main>
      </div>
      <DmWidget />
      <OfflineIndicator />
      <MustChangePasswordDialog />
      {showDemoTour && <DemoTour onClose={closeDemoTour} />}
      {showOnboarding && (
        <OnboardingWizard
          open={showOnboarding}
          onClose={async () => {
            setShowOnboarding(false);
            await fetchMe();
          }}
        />
      )}
    </div>
  );
}
